import { Award, Sparkles, FileText, Plus, ExternalLink } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';

const PlansStep = ({ navigate }) => (
  <div className="space-y-6">
    <div className="text-center mb-6">
      <div className="bg-gradient-to-br from-purple-100 to-purple-200 p-4 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
        <Award className="h-8 w-8 text-purple-600" />
      </div>
      <h2 className="text-2xl font-bold text-gray-900 mb-2">Create Your First Membership Plan</h2>
      <p className="text-gray-600">
        Choose from three easy ways to set up your membership offerings
      </p>
    </div>

    {/* Three Options */}
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      {/* Option 1: AI Suggestions */}
      <Card className="border-2 border-purple-200 hover:border-purple-400 transition-all hover:shadow-lg">
        <CardContent className="pt-6">
          <div className="text-center mb-4">
            <div className="bg-gradient-to-br from-purple-100 to-pink-100 w-14 h-14 rounded-full flex items-center justify-center mx-auto">
              <Sparkles className="h-7 w-7 text-purple-600" />
            </div>
          </div>
          
          <h3 className="font-bold text-gray-900 mb-2 text-center">AI Suggestions</h3>
          <p className="text-sm text-gray-600 mb-4 text-center">
            Let AI create optimized plans based on your specialty and market
          </p>
          
          <ul className="text-xs text-gray-600 space-y-2 mb-4">
            <li className="flex items-center gap-2">
              <span className="text-purple-600">✓</span>
              <span>3 tiered plans instantly</span>
            </li>
            <li className="flex items-center gap-2">
              <span className="text-purple-600">✓</span>
              <span>Market-based pricing</span>
            </li>
            <li className="flex items-center gap-2">
              <span className="text-purple-600">✓</span>
              <span>Revenue projections</span>
            </li>
          </ul>
          
          <Button 
            onClick={() => navigate('/admin/templates')}
            className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700"
            size="sm"
          >
            <Sparkles className="h-4 w-4 mr-2" />
            Use AI
          </Button>
        </CardContent>
      </Card>

      {/* Option 2: Templates */}
      <Card className="border-2 border-teal-200 hover:border-teal-400 transition-all hover:shadow-lg">
        <CardContent className="pt-6">
          <div className="text-center mb-4">
            <div className="bg-gradient-to-br from-teal-100 to-cyan-100 w-14 h-14 rounded-full flex items-center justify-center mx-auto">
              <FileText className="h-7 w-7 text-teal-600" />
            </div>
          </div>
          
          <h3 className="font-bold text-gray-900 mb-2 text-center">Pre-Built Templates</h3>
          <p className="text-sm text-gray-600 mb-4 text-center">
            Choose from proven membership plan templates
          </p>
          
          <ul className="text-xs text-gray-600 space-y-2 mb-4">
            <li className="flex items-center gap-2">
              <span className="text-teal-600">✓</span>
              <span>Healthcare Essentials</span>
            </li>
            <li className="flex items-center gap-2">
              <span className="text-teal-600">✓</span>
              <span>Family Plus packages</span>
            </li>
            <li className="flex items-center gap-2">
              <span className="text-teal-600">✓</span>
              <span>Premium Care tiers</span>
            </li>
          </ul>
          
          <Button 
            onClick={() => navigate('/admin/templates')}
            className="w-full bg-teal-600 hover:bg-teal-700"
            size="sm"
          >
            <FileText className="h-4 w-4 mr-2" />
            Browse Templates
          </Button>
        </CardContent>
      </Card>

      {/* Option 3: Custom */}
      <Card className="border-2 border-blue-200 hover:border-blue-400 transition-all hover:shadow-lg">
        <CardContent className="pt-6">
          <div className="text-center mb-4">
            <div className="bg-gradient-to-br from-blue-100 to-indigo-100 w-14 h-14 rounded-full flex items-center justify-center mx-auto">
              <Plus className="h-7 w-7 text-blue-600" />
            </div>
          </div>
          
          <h3 className="font-bold text-gray-900 mb-2 text-center">Create Custom</h3>
          <p className="text-sm text-gray-600 mb-4 text-center">
            Build your own plans from scratch with full control
          </p>
          
          <ul className="text-xs text-gray-600 space-y-2 mb-4">
            <li className="flex items-center gap-2">
              <span className="text-blue-600">✓</span>
              <span>Custom pricing</span>
            </li>
            <li className="flex items-center gap-2">
              <span className="text-blue-600">✓</span>
              <span>Unique benefits</span>
            </li>
            <li className="flex items-center gap-2">
              <span className="text-blue-600">✓</span>
              <span>Complete flexibility</span>
            </li>
          </ul>
          
          <Button 
            onClick={() => navigate('/admin/plans')}
            className="w-full bg-blue-600 hover:bg-blue-700"
            size="sm"
          >
            <Plus className="h-4 w-4 mr-2" />
            Create Plan
          </Button>
        </CardContent>
      </Card>
    </div>

    {/* Info Box */}
    <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
      <h4 className="font-semibold text-amber-900 mb-2">🎯 Recommended Approach</h4>
      <p className="text-sm text-amber-800 mb-2">
        Start with <strong>AI Suggestions</strong> to get market-optimized plans instantly, then customize as needed!
      </p>
      <p className="text-xs text-amber-700">
        You can always edit, delete, or add more plans later from your Plans dashboard.
      </p>
    </div>

    {/* Quick Start Guide */}
    <Card className="border-2 border-gray-200">
      <CardContent className="pt-6">
        <h4 className="font-semibold text-gray-900 mb-3">Quick Start Guide</h4>
        <div className="space-y-3 text-sm">
          <div className="flex gap-3">
            <div className="bg-teal-100 text-teal-700 rounded-full w-6 h-6 flex items-center justify-center flex-shrink-0 font-bold text-xs">
              1
            </div>
            <div>
              <p className="font-medium text-gray-900">Choose Your Method</p>
              <p className="text-gray-600 text-xs">Select AI, Template, or Custom based on your preference</p>
            </div>
          </div>
          
          <div className="flex gap-3">
            <div className="bg-teal-100 text-teal-700 rounded-full w-6 h-6 flex items-center justify-center flex-shrink-0 font-bold text-xs">
              2
            </div>
            <div>
              <p className="font-medium text-gray-900">Review & Customize</p>
              <p className="text-gray-600 text-xs">Adjust pricing, benefits, and details as needed</p>
            </div>
          </div>
          
          <div className="flex gap-3">
            <div className="bg-teal-100 text-teal-700 rounded-full w-6 h-6 flex items-center justify-center flex-shrink-0 font-bold text-xs">
              3
            </div>
            <div>
              <p className="font-medium text-gray-900">Activate Plans</p>
              <p className="text-gray-600 text-xs">Make plans available to patients immediately</p>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  </div>
);

export default PlansStep;
